import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { htmlSafe } from '@ember/template';
import { Section, VStack } from 'tio-reimagined/components/layout';
import ProgressBar from 'tio-reimagined/components/progress-bar';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import MatchScoreBadge from 'tio-reimagined/components/match-score-badge';
import SkillChip from 'tio-reimagined/components/skill-chip';
import TaEligibilityBadge from 'tio-reimagined/components/ta-eligibility-badge';
import ActionableCard from 'tio-reimagined/components/actionable-card';
import { eq } from 'tio-ui/utilities';
import PageHero from 'tio-reimagined/components/page-hero';
import {
  PhArrowLeft,
  PhArrowRight,
  PhTarget,
  PhWarning,
  PhSparkle,
  PhGraduationCap,
  PhCertificate,
  PhClock,
  PhCurrencyDollar,
  PhStar,
  PhRocket,
  PhPath,
  PhBuildings,
  PhBriefcase,
  PhChartBar,
  PhHeart,
  PhPlay,
  PhBookOpen,
  PhVideo,
} from 'ember-phosphor-icons';
import type { CareerSummary, SkillGap } from 'tio-employee/services/career-pathways-api';

interface CareerPathwaysSkillsGapsSignature {
  Args: {
    model: {
      skillGaps: SkillGap[];
      summary: CareerSummary;
    };
  };
}

export default class CareerPathwaysSkillsGaps extends Component<CareerPathwaysSkillsGapsSignature> {
  @tracked selectedGap?: SkillGap;
  @tracked savedPaths: string[] = [];
  @tracked expandedPath: string | null = null;

  get skillGaps() {
    return this.args.model.skillGaps;
  }

  get summary() {
    return this.args.model.summary;
  }

  get activeGap() {
    return (
      this.selectedGap ??
      this.skillGaps[0] ?? {
        id: 'placeholder-gap',
        skillId: 'unknown',
        name: 'Skill Gap',
        importance: 'Important',
        currentLevel: 0,
        requiredLevel: 0,
        targetRole: 'Your target role',
        description: 'No gap details available yet.',
        whyNeeded: 'Add a target role to see personalized gap details.',
        timeToClose: 'TBD',
        learningPaths: [],
      }
    );
  }

  selectGap = (gap: SkillGap) => {
    this.selectedGap = gap;
    this.expandedPath = null;
  };

  togglePathExpand = (pathId: string) => {
    if (this.expandedPath === pathId) {
      this.expandedPath = null;
    } else {
      this.expandedPath = pathId;
    }
  };

  toggleSaved = (pathId: string) => {
    if (this.savedPaths.includes(pathId)) {
      this.savedPaths = this.savedPaths.filter((id) => id !== pathId);
    } else {
      this.savedPaths = [...this.savedPaths, pathId];
    }
  };

  isSaved = (pathId: string) => {
    return this.savedPaths.includes(pathId);
  };

  getImportanceIntent = (importance: string) => {
    if (importance === 'Critical') return 'danger';
    if (importance === 'Important') return 'warning';
    return 'default';
  };

  getProgressStyle = (level: number) => {
    return htmlSafe(`width: ${level}%`);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0">
      {{! Back navigation }}
      <div class="mb-4">
        <LinkTo
          @route="authenticated.reimagined.skills"
          class="inline-flex items-center gap-2 tio-fg-brand hover:tio-fg-brand font-medium"
        >
          <PhArrowLeft @size={{18}} />
          Back to Skills Profile
        </LinkTo>
      </div>

      {{! Header }}
      <PageHero @gradient="warm">
        <:default>
          <div class="flex flex-col lg:flex-row items-start justify-between gap-6">
            <div class="flex-1 min-w-0">
              <div class="tio-row mb-3">
                <PhTarget @size={{32}} class="shrink-0" />
                <Heading @level={{1}} class="text-white text-2xl">Close Your Skill Gaps</Heading>
              </div>
              <Text class="text-red-100 text-lg mb-4">
                You have
                {{this.summary.totalGaps}}
                skill gaps preventing you from reaching your goal. Here's your personalized plan to
                close them.
              </Text>
              <div class="flex flex-wrap items-center gap-6 text-sm">
                <div class="tio-row-tight">
                  <PhWarning @size={{18}} class="text-yellow-300" />
                  <span>{{this.summary.criticalGaps}} Critical Gap</span>
                </div>
                <div class="tio-row-tight">
                  <PhClock @size={{18}} />
                  <span>~{{this.summary.avgTimeToClose}} to close all</span>
                </div>
                <div class="tio-row-tight">
                  <PhGraduationCap @size={{18}} />
                  <span>{{this.summary.totalLearningOptions}} learning options</span>
                </div>
              </div>
            </div>

            {{! TA Eligibility Card }}
            <div class="bg-white/15 backdrop-blur rounded-xl p-5 shrink-0">
              <div class="tio-row-tight mb-2">
                <PhCurrencyDollar @size={{20}} class="text-green-300" />
                <span class="font-semibold">Your TA Benefit</span>
              </div>
              <div class="text-3xl font-bold">{{this.summary.maxTaCoverage}}</div>
              <Text @size="sm" class="text-red-100">
                {{this.summary.taEligibleOptions}}
                eligible options available
              </Text>
            </div>
          </div>
        </:default>
      </PageHero>

      <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 min-w-0">
        {{! Skill Gaps List (Left sidebar) }}
        <div class="lg:col-span-4 space-y-4">
          <Section class="bg-white rounded-lg shadow-sm">
            <:header>
              <div class="tio-row-tight">
                <PhChartBar @size={{18}} class="tio-fg-danger" />
                <span>Your Skill Gaps</span>
              </div>
            </:header>
            <:body>
              <div class="space-y-3">
                {{#each this.skillGaps as |gap|}}
                  <button
                    type="button"
                    {{on "click" (fn this.selectGap gap)}}
                    class="w-full text-left p-4 rounded-lg border-2 transition-all
                      {{if
                        (eq this.activeGap.id gap.id)
                        'border-violet-500 tio-bg-brand-wash'
                        'tio-border-default hover:border-violet-300'
                      }}"
                  >
                    <div class="flex items-start justify-between mb-2">
                      <div class="font-semibold tio-fg-primary">{{gap.name}}</div>
                      <Chip @intent={{this.getImportanceIntent gap.importance}} @size="sm">
                        {{gap.importance}}
                      </Chip>
                    </div>

                    {{! Progress bar showing current vs required }}
                    <div class="mb-2">
                      <div class="flex justify-between text-xs tio-fg-muted mb-1">
                        <span>Current: {{gap.currentLevel}}%</span>
                        <span>Required: {{gap.requiredLevel}}%</span>
                      </div>
                      <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          class="h-full bg-gradient-to-r from-red-400 to-red-500 rounded-full"
                          style={{this.getProgressStyle gap.currentLevel}}
                        ></div>
                      </div>
                    </div>

                    <div class="tio-row-tight text-xs tio-fg-muted">
                      <PhClock @size={{12}} />
                      <span>{{gap.timeToClose}} to close</span>
                      <span>•</span>
                      <span>{{gap.learningPaths.length}} options</span>
                    </div>
                  </button>
                {{/each}}
              </div>
            </:body>
          </Section>

          {{! Goal Progress Card }}
          <Section class="bg-white rounded-lg shadow-sm">
            <:header>
              <div class="tio-row-tight">
                <PhRocket @size={{18}} class="tio-fg-brand" />
                <span>Your Goal</span>
              </div>
            </:header>
            <:body>
              <div class="text-center py-2">
                <div
                  class="w-16 h-16 mx-auto mb-3 rounded-full tio-bg-brand-soft flex items-center justify-center"
                >
                  <PhBriefcase @size={{28}} class="tio-fg-brand" />
                </div>
                <Heading @level={{4}}>{{this.activeGap.targetRole}}</Heading>
                <Text @variant="muted" @size="sm" class="mb-3">
                  Target Role
                </Text>
                <div class="tio-bg-sunken rounded-lg p-3">
                  <div class="flex justify-between text-sm mb-1">
                    <span>Skills Ready</span>
                    <span class="font-semibold">9 / 12</span>
                  </div>
                  <ProgressBar @value={{75}} @color="blue" />
                </div>
              </div>
            </:body>
          </Section>
        </div>

        {{! Main Content - Selected Gap Detail }}
        <div class="lg:col-span-8 space-y-4">
          {{! Gap Overview Card }}
          <Section class="bg-white rounded-lg shadow-sm">
            <:header>
              <div class="flex items-center justify-between">
                <div class="tio-row">
                  <div
                    class="w-10 h-10 rounded-full flex items-center justify-center
                      {{if
                        (eq this.activeGap.importance 'Critical')
                        'tio-bg-danger-soft tio-fg-danger'
                        'tio-bg-warning-soft tio-fg-warning'
                      }}"
                  >
                    <PhTarget @size={{20}} />
                  </div>
                  <div>
                    <Heading @level={{2}}>{{this.activeGap.name}}</Heading>
                    <Text @variant="muted" @size="sm">Required for
                      {{this.activeGap.targetRole}}</Text>
                  </div>
                </div>
                <Chip
                  @intent={{this.getImportanceIntent
                    (if this.activeGap.importance this.activeGap.importance "default")
                  }}
                >
                  {{this.activeGap.importance}}
                </Chip>
              </div>
            </:header>
            <:body>
              <div class="space-y-4">
                <div>
                  <Text class="tio-fg-secondary">{{this.activeGap.description}}</Text>
                </div>

                <div class="tio-bg-brand-wash border tio-border-brand-soft rounded-lg p-4">
                  <div class="tio-row-top">
                    <PhSparkle @size={{20}} class="tio-fg-brand shrink-0 mt-0.5" />
                    <div>
                      <Text @size="sm" class="font-semibold tio-fg-brand">Why this matters</Text>
                      <Text @size="sm" class="tio-fg-brand">{{this.activeGap.whyNeeded}}</Text>
                    </div>
                  </div>
                </div>

                {{! Gap Progress Visualization }}
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div class="text-center p-3 tio-bg-sunken rounded-lg">
                    <div
                      class="text-2xl font-bold tio-fg-danger"
                    >{{this.activeGap.currentLevel}}%</div>
                    <Text @variant="muted" @size="xs">Current Level</Text>
                  </div>
                  <div class="text-center p-3 tio-bg-sunken rounded-lg relative">
                    <PhArrowRight @size={{24}} class="tio-fg-disabled mx-auto" />
                    <Text @variant="muted" @size="xs" class="mt-1">Gap to Close</Text>
                  </div>
                  <div class="text-center p-3 tio-bg-sunken rounded-lg">
                    <div
                      class="text-2xl font-bold tio-fg-success"
                    >{{this.activeGap.requiredLevel}}%</div>
                    <Text @variant="muted" @size="xs">Target Level</Text>
                  </div>
                </div>
              </div>
            </:body>
          </Section>

          {{! Learning Paths }}
          <Section class="bg-white rounded-lg shadow-sm">
            <:header>
              <div class="flex items-center justify-between">
                <div class="tio-row-tight">
                  <PhGraduationCap @size={{18}} class="tio-fg-brand" />
                  <span>Recommended Learning Paths</span>
                </div>
                <Text @variant="muted" @size="sm">
                  {{this.activeGap.learningPaths.length}}
                  options
                </Text>
              </div>
            </:header>
            <:body>
              <div class="space-y-4">
                {{#each this.activeGap.learningPaths as |path index|}}
                  <ActionableCard
                    @showBanner={{eq index 0}}
                    @bannerText="Best Match for You"
                    @bannerClass="bg-gradient-to-r from-violet-500 to-purple-500 text-white text-sm font-medium px-4 py-2 tio-row-tight"
                    @cardClass={{if
                      (eq index 0)
                      "bg-white rounded-xl border border-violet-300 ring-2 ring-violet-100 shadow-sm overflow-hidden"
                      "bg-white rounded-xl border tio-border-default shadow-sm overflow-hidden hover:border-violet-300 transition-all"
                    }}
                  >
                    <:header>
                      <div
                        class="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4"
                      >
                        <div class="flex-1 min-w-0">
                          <div class="flex flex-wrap items-center gap-2 mb-2">
                            <Heading @level={{4}} class="tio-fg-primary">
                              {{path.title}}
                            </Heading>
                          </div>
                          <div
                            class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm tio-fg-muted"
                          >
                            <span class="tio-row-extra-tight">
                              <PhBuildings @size={{14}} />
                              {{path.provider}}
                            </span>
                            <span class="tio-row-extra-tight">
                              <PhCertificate @size={{14}} />
                              {{path.type}}
                            </span>
                            <span class="tio-row-extra-tight">
                              <PhClock @size={{14}} />
                              {{path.duration}}
                            </span>
                            <span class="tio-row-extra-tight">
                              {{#if (eq path.format "Online")}}
                                <PhVideo @size={{14}} />
                              {{else}}
                                <PhBookOpen @size={{14}} />
                              {{/if}}
                              {{path.format}}
                            </span>
                          </div>
                        </div>

                        {{! Match Score }}
                        <div class="tio-row">
                          <button
                            type="button"
                            {{on "click" (fn this.toggleSaved path.id)}}
                            class="p-2 rounded-full transition-colors
                              {{if
                                (this.isSaved path.id)
                                'text-pink-500 bg-pink-50'
                                'tio-fg-disabled hover:text-pink-500 hover:bg-pink-50'
                              }}"
                          >
                            <PhHeart
                              @size={{20}}
                              @weight={{if (this.isSaved path.id) "fill" "regular"}}
                            />
                          </button>
                          <MatchScoreBadge @score={{path.matchScore}} @label="Match" @size="md" />
                        </div>
                      </div>
                    </:header>

                    <:skills>
                      {{#each path.skillsGained as |skill|}}
                        <SkillChip @name={{skill}} @intent="default" @showLevel={{false}} />
                      {{/each}}
                    </:skills>

                    <:eligibility>
                      <Text @size="sm" class="tio-fg-secondary">{{path.description}}</Text>
                      <div class="mt-3">
                        <TaEligibilityBadge @eligible={{path.taEligible}} />
                      </div>
                    </:eligibility>

                    <:actions>
                      <div class="tio-row-extra-loose">
                        <div>
                          <Text @variant="muted" @size="xs">Cost</Text>
                          <div
                            class="font-semibold
                              {{if (eq path.cost 'Free') 'tio-fg-success' 'tio-fg-primary'}}"
                          >
                            {{path.cost}}
                          </div>
                        </div>
                        <div>
                          <Text @variant="muted" @size="xs">Reviews</Text>
                          <div class="tio-row-extra-tight">
                            <PhStar @size={{14}} class="text-yellow-500" @weight="fill" />
                            <span class="font-semibold">{{path.reviews.rating}}</span>
                            <span class="tio-fg-muted text-sm">({{path.reviews.count}})</span>
                          </div>
                        </div>
                      </div>

                      <div class="tio-row-tight">
                        <Button @intent="default" @appearance="outlined">
                          Learn More
                        </Button>
                        {{#if path.taEligible}}
                          <Button @intent="primary" @icon={{PhPlay}} @label="Apply with TA" />
                        {{else}}
                          <Button @intent="primary" @icon={{PhPlay}} @label="Enroll Now" />
                        {{/if}}
                      </div>
                    </:actions>
                  </ActionableCard>
                {{/each}}
              </div>
            </:body>
          </Section>

          {{! Next Steps CTA }}
          <div class="bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl p-6 text-white">
            <div
              class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            >
              <div>
                <Heading @level={{3}} class="text-white mb-1">Ready to close this gap?</Heading>
                <Text class="text-violet-200">
                  Add the best-match course to your career plan and track your progress.
                </Text>
              </div>
              <div class="flex gap-3">
                <LinkTo
                  @route="authenticated.reimagined.plan.new"
                  class="inline-flex items-center gap-2 px-4 py-2 bg-white tio-fg-brand rounded-lg font-medium hover:tio-bg-brand-wash transition-colors"
                >
                  <PhPath @size={{18}} />
                  Add to Plan
                </LinkTo>
              </div>
            </div>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
